import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  HttpStatusCode,
  ReCaptchaInterceptor,
  RETRY_REQUEST_CONFIG,
  RetryRequestConfig,
  RetryRequestInterceptor,
} from '@core/data-access';
import { ReCaptchaModule } from '@core/recaptcha/feature';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyPortalFormModule } from '@portal/form/feature';
import { GenericApiModule } from '@core/generic-api/data-access';
import { PortalSharedIconModule } from '@portal/icon';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import * as fromStore from '@bizflex/data-access/store';
import { AuthInterceptor } from '@bizflex/data-access';
import { DEFAULT_CONFIG } from '@portal/data-access';
import { environment } from '../environments/environment';
import { OverlayModule } from '@angular/cdk/overlay';
import { ActionReducer, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { PortalApplicationFormModule } from '@portal/application/form';
import { UserIdleModule } from '@core/user-idle';
import { ERROR_PAGE_CODE_EXCLUDE, ErrorPageInterceptor } from '@bizflex/error-page';
import { MatDialogModule } from '@angular/material/dialog';
import { AppConfigModule } from '@core/app-config/feature';

export const ngxMaskConfig: () => Partial<IConfig> = () => ({
  validation: false,
  thousandSeparator: ',',
  dropSpecialCharacters: false,
});

// console.log all actions
function debug(reducer: ActionReducer<any>): ActionReducer<any> {
  return function (state, action) {
    // console.group('@ngrx/store:', action.type);
    // console.log('state', state);
    // console.log('action', action);
    // console.groupEnd();
    return reducer(state, action);
  };
}

const errorPageCodeExclude: HttpStatusCode[] = [HttpStatusCode.C400, HttpStatusCode.C404, HttpStatusCode.C500];
const retryRequestConfig: RetryRequestConfig = {
  maxRetries: 3,
  delayMs: 3000,
  codeExclude: [HttpStatusCode.C401, HttpStatusCode.C403, HttpStatusCode.C404],
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    MatDialogModule,
    BrowserModule,
    BrowserAnimationsModule,
    OverlayModule,
    HttpClientModule,
    AppRoutingModule,
    NgxMaskModule.forRoot(ngxMaskConfig),
    PortalSharedIconModule.forRoot(),
    FormlyModule.forRoot({
      extras: { lazyRender: true, resetFieldOnHide: true },
    }),
    FormlyPortalFormModule,
    PortalApplicationFormModule,
    GenericApiModule.forRoot(),
    ReCaptchaModule.forRoot(),
    AppConfigModule.forRoot({
      configUrl: environment.apiConfig,
      initial: {
        apiHost: '',
        appId: 'flexipay_portal',
        environment: environment.name,
        recaptchaSitekey: '',
        storageDrive: 's3',
        uploadFolder: 'cimb-sme-files-storage-local',
        urlPrefix: '/api/',
      },
    }),
    StoreModule.forRoot(
      {
        [fromStore.BIZFLEX_FEATURE_KEY]: fromStore.bizflexReducer,
      },
      {
        metaReducers: !environment.production ? [debug] : [],
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      }
    ),
    EffectsModule.forRoot([fromStore.BizflexEffects]),
    !environment.production
      ? StoreDevtoolsModule.instrument({
          name: 'FlexiPay Portal',
          maxAge: 25, // Retains last 25 states
          logOnly: !environment.production, // Restrict extension to log-only mode
          autoPause: true, // Pauses recording actions and state changes when the extension window is not open
        })
      : [],

    UserIdleModule.forRoot({ idle: DEFAULT_CONFIG.TIME_OUT_IN_SECONDS, timeout: 3, ping: 60 }),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ReCaptchaInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RetryRequestInterceptor,
      multi: true,
    },
    {
      provide: RETRY_REQUEST_CONFIG,
      useValue: retryRequestConfig,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorPageInterceptor,
      multi: true,
    },
    {
      provide: ERROR_PAGE_CODE_EXCLUDE,
      useValue: errorPageCodeExclude,
    },
    fromStore.BizflexFacade,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
